import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Segmented, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../api/manufacturing";
import { isoDate } from "../../api/reporting";
import type { OrderDTO } from "../../api/order";
import { WarehouseClient } from "../../api/product";
import {
  SERVICE_INTERNAL_REASONS,
  SERVICE_ORDER_KINDS,
  SERVICE_ORDER_STATUSES,
  ServiceMotiveClient,
  ServiceOrderClient,
  type ServiceInternalReason,
  type ServiceOrderDTO,
  type ServiceOrderKind,
} from "../../api/servicedesk";
import { NexpimView } from "../../views";
import { internalReasonLabel, ServiceStatusBadge, serviceStatusLabel } from "./badges";
import { LineDetailsFields, lineFrom, missingForLines, type LineDetails } from "./lineDetails";
import { NexeraOrderLookup } from "./NexeraOrderLookup";
import { ServicePartsPicker, type PickedPart } from "./ServicePartsPicker";

/**
 * Service orders, paged in the database. Opens on the open ones — the desk's
 * working list; the imported history (15,600 orders since 2021) is a filter
 * away. The search box covers the retailer's reference, the client, the customer, their
 * email, the order number and the SKUs on it.
 */
export function ServiceOrdersPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const navigate = useNavigate();
  const allowed = hasView(NexpimView.SERVICE_DESK);
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const [creating, setCreating] = useState(false);

  const table = useServerTable<ServiceOrderDTO>({
    queryKey: ["servicedesk", "service-order"],
    fetchPage: (request) => orders.find(request),
    fetchTotal: () => orders.count(),
    enabled: allowed,
  });

  const cols = useMemo(() => {
    const c = defineColumns<ServiceOrderDTO>();
    return [
      c.number("number", (row) => row.number, { headerKey: "column.sd.number", filter: "range", priority: 1 }),
      c.text("order_date", (row) => row.order_date ?? "", { headerKey: "column.sd.date", filter: "daterange", priority: 2 }),
      c.custom("status", (row) => row.status, (row) => <ServiceStatusBadge status={row.status} />, {
        headerKey: "column.status",
        filter: "multiselect",
        filterOptions: SERVICE_ORDER_STATUSES.map((value) => ({ value, label: serviceStatusLabel(t, value) })),
        priority: 2,
      }),
      c.custom(
        "kind",
        (row) => row.kind,
        (row) =>
          row.kind === "INTERNAL" ? (
            <Badge variant="info" size="sm">
              {t("sd.kind.internal", "Internal")}
            </Badge>
          ) : (
            <span className="text-[12px] text-ink-muted">{t("sd.kind.order", "Customer order")}</span>
          ),
        {
          headerKey: "column.sd.kind",
          filter: "multiselect",
          filterOptions: SERVICE_ORDER_KINDS.map((value) => ({
            value,
            label: value === "INTERNAL" ? t("sd.kind.internal", "Internal") : t("sd.kind.order", "Customer order"),
          })),
          priority: 3,
        },
      ),
      c.text(
        "client_name",
        (row) =>
          row.kind === "INTERNAL"
            ? row.internal_reason
              ? internalReasonLabel(t, row.internal_reason)
              : ""
            : (row.client_name ?? t("sd.no-client", "No client")),
        { headerKey: "column.sd.client", priority: 1 },
      ),
      c.text("client_number", (row) => row.client_number ?? "", { headerKey: "column.sd.client-number", defaultVisible: false }),
      c.text("client_order_reference", (row) => row.client_order_reference ?? "", { headerKey: "column.sd.reference", priority: 2 }),
      c.text("ship_name", (row) => row.ship_name ?? "", { headerKey: "column.sd.customer", priority: 1 }),
      c.text("ship_city", (row) => row.ship_city ?? "", { headerKey: "column.sd.city", priority: 4 }),
      c.text("products", (row) => row.products ?? "", { headerKey: "column.sd.products", priority: 3 }),
      c.number("open_line_count", (row) => row.open_line_count, { headerKey: "column.sd.open-lines", filter: "range", defaultVisible: false }),
      c.text("last_shipment_date", (row) => row.last_shipment_date ?? "", {
        headerKey: "column.sd.last-shipment",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.number("nexera_order_number", (row) => row.nexera_order_number, { headerKey: "column.sd.nexera-order", filter: "range", defaultVisible: false }),
      c.text("ship_email", (row) => row.ship_email ?? "", { headerKey: "column.sd.email", defaultVisible: false }),
      c.text("sigm_agent", (row) => row.sigm_agent ?? "", { headerKey: "column.sd.agent", filter: "none", defaultVisible: false }),
      c.text("warehouse_code", (row) => row.warehouse_code ?? "", { headerKey: "column.stock.warehouse", filter: "none", defaultVisible: false }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.service-orders", "Service orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.service-orders", "Service orders")}
        subtitle={t("sd.subtitle", "Replacement parts sent to customers, and why.")}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t("sd.new", "New service order")}
            </Button>
          ) : null
        }
      />
      <DataTable<ServiceOrderDTO>
        data={table.rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/servicedesk/orders"
        loading={table.loading}
        server={table.mode}
        initialFilters={[{ id: "status", value: ["OPEN"] }]}
        onRowClick={(row) => void navigate({ to: "/servicedesk/orders/$orderId", params: { orderId: String(row.id) } })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      {creating && <NewServiceOrderDialog onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * A new service order and its parts in one step.
 *
 * <p>Order: the customer's request. Picking the Nexera sale fills the ship-to
 * and lists what was sold, so the parts are ticked right here; everything can
 * still be typed by hand. Internal: no sale and no customer, only why it is
 * needed and who asked. Either way each part says whether the service shelf
 * can send it, and the order is written with its lines or not at all.</p>
 */
function NewServiceOrderDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState<ServiceOrderKind>("ORDER");
  const [warehouseId, setWarehouseId] = useState("");
  const [reference, setReference] = useState("");
  const [nexera, setNexera] = useState<OrderDTO | null>(null);
  const [ship, setShip] = useState({ name: "", address: "", city: "", postal: "", phone: "", email: "" });
  const [reason, setReason] = useState<ServiceInternalReason | "">("");
  const [requestedBy, setRequestedBy] = useState("");
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState<Record<string, PickedPart>>({});
  const [details, setDetails] = useState<LineDetails>({ motiveId: "", run: "", note: "" });
  const internal = kind === "INTERNAL";

  // What the order's client will be, by the server's own rule (the sale behind a replacement order).
  const saleClient = useQuery({
    queryKey: ["servicedesk", "sale-client", nexera?.no ?? null],
    queryFn: () => new ServiceOrderClient(http).saleClient(Number(nexera!.no)),
    enabled: !internal && nexera?.no != null,
    staleTime: 5 * 60_000,
  });
  const motives = useQuery({
    queryKey: ["servicedesk", "service-motive"],
    queryFn: () => new ServiceMotiveClient(http).findAll(),
    staleTime: 10 * 60_000,
  });
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const shipping = (warehouses.data ?? []).filter((w) => w.is_active && (w.kind === "SERVICE" || w.kind === "MANUFACTURING"));
  const effectiveWarehouse = warehouseId || String(shipping.find((w) => w.kind === "SERVICE")?.id ?? "");

  const pick = (order: OrderDTO) => {
    setNexera(order);
    setReference(order.client_order_no ?? reference);
    setShip({
      name: order.ship_name ?? "",
      address: order.ship_address ?? "",
      city: order.ship_city ?? "",
      postal: order.ship_postal_code ?? "",
      phone: order.ship_phone ?? "",
      email: order.ship_email ?? "",
    });
  };

  const text = (value: string) => (value.trim() === "" ? null : value.trim());
  const chosen = Object.values(picked);
  const linesMissing = missingForLines(t, chosen, details);
  const headerMissing = !effectiveWarehouse
    ? t("sd.new.missing-warehouse", "Choose where it ships from.")
    : internal
      ? reason === ""
        ? t("sd.new.missing-reason", "Choose why it is needed.")
        : null
      : text(ship.name) == null
        ? t("sd.new.missing-customer", "Enter the customer.")
        : null;
  const missing = headerMissing ?? linesMissing;

  const create = useMutation({
    mutationFn: () => {
      const saleNumber = internal || nexera?.no == null ? null : Number(nexera.no);
      return new ServiceOrderClient(http).createWithLines(
        {
          id: 0,
          number: 0,
          status: "OPEN",
          kind,
          internal_reason: internal && reason !== "" ? reason : null,
          requested_by: internal ? text(requestedBy) : null,
          warehouse_id: Number(effectiveWarehouse),
          order_date: isoDate(new Date()),
          client_order_reference: internal ? null : text(reference),
          nexera_order_number: saleNumber,
          // Set by the server from the sale.
          client_id: null,
          ship_name: text(ship.name),
          ship_name_2: null,
          ship_address: text(ship.address),
          ship_city: text(ship.city),
          ship_postal_code: text(ship.postal),
          ship_phone: text(ship.phone),
          ship_email: text(ship.email),
          note: text(note),
        },
        chosen.map((part, index) => lineFrom(part, 0, index + 1, details)),
      );
    },
    onSuccess: (order) => {
      onClose();
      void navigate({ to: "/servicedesk/orders/$orderId", params: { orderId: String(order.id) } });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const field = (id: keyof typeof ship, label: string, required = false) => (
    <Field label={label} htmlFor={`sd-new-${id}`} required={required}>
      <Input id={`sd-new-${id}`} value={ship[id]} onChange={(event) => setShip({ ...ship, [id]: event.target.value })} />
    </Field>
  );

  return (
    <Dialog
      open
      size="xl"
      onClose={onClose}
      title={t("sd.new", "New service order")}
      actions={
        <>
          {missing && <span className="mr-auto text-[12px] text-ink-muted">{missing}</span>}
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={missing != null} loading={create.isPending} onClick={() => create.mutate()}>
            {chosen.length > 0
              ? t("sd.create-with-parts", "Create order with {{n}} parts", { n: chosen.length })
              : t("sd.create", "Create order")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            value={kind}
            onChange={(value) => setKind(value as ServiceOrderKind)}
            options={[
              { value: "ORDER", label: t("sd.kind.order", "Customer order") },
              { value: "INTERNAL", label: t("sd.kind.internal", "Internal") },
            ]}
          />
          <span className="text-[12px] text-ink-muted">
            {internal
              ? t("sd.kind.internal.hint", "No sale and no customer: parts for the plant, the showroom or the service shelf.")
              : t("sd.kind.order.hint", "A customer's request, linked to the Nexera sale when it is found.")}
          </span>
        </div>

        {internal ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t("sd.reason", "Why")} htmlFor="sd-new-reason" required>
              <Select
                id="sd-new-reason"
                value={reason}
                placeholder={t("form.choose", "Choose…")}
                options={SERVICE_INTERNAL_REASONS.map((value) => ({ value, label: internalReasonLabel(t, value) }))}
                onChange={(event) => setReason(event.target.value as ServiceInternalReason)}
              />
            </Field>
            <Field label={t("sd.requested-by", "Requested by")} htmlFor="sd-new-requested">
              <Input id="sd-new-requested" value={requestedBy} onChange={(event) => setRequestedBy(event.target.value)} />
            </Field>
            <Field label={t("sd.ship-from", "Ship from")} htmlFor="sd-new-warehouse" required>
              <Select
                id="sd-new-warehouse"
                value={effectiveWarehouse}
                options={shipping.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
                onChange={(event) => setWarehouseId(event.target.value)}
              />
            </Field>
            <Field label={t("form.field.note", "Note")} htmlFor="sd-new-note" className="sm:col-span-3">
              <Input id="sd-new-note" value={note} onChange={(event) => setNote(event.target.value)} />
            </Field>
          </div>
        ) : (
          <>
            <Field label={t("sd.find-sale", "Find the sale in Nexera")} htmlFor="sd-lookup">
              <NexeraOrderLookup onPick={pick} />
            </Field>
            <p className="text-[12px] text-ink-muted">
              {nexera
                ? saleClient.data !== undefined
                  ? t("sd.linked.client", "Linked to Nexera order #{{n}}: the client is {{client}}.", {
                      n: nexera.no,
                      client: saleClient.data ? saleClient.data.client_name : t("sd.no-client", "No client"),
                    })
                  : t("sd.linked", "Linked to Nexera order #{{n}}. The client is taken from the sale.", { n: nexera.no })
                : t("sd.unlinked", "Not linked to a sale: the order will have no client.")}
              {nexera && (
                <Button variant="ghost" size="sm" className="ml-2" onClick={() => setNexera(null)}>
                  {t("sd.unlink", "Unlink")}
                </Button>
              )}
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={t("sd.ship-from", "Ship from")} htmlFor="sd-new-warehouse" required>
                <Select
                  id="sd-new-warehouse"
                  value={effectiveWarehouse}
                  options={shipping.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }))}
                  onChange={(event) => setWarehouseId(event.target.value)}
                />
              </Field>
              <Field label={t("column.sd.reference", "Retailer order")} htmlFor="sd-new-reference">
                <Input id="sd-new-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
              </Field>
              {field("name", t("column.sd.customer", "Customer"), true)}
              {field("address", t("sd.address", "Address"))}
              {field("city", t("column.sd.city", "City"))}
              {field("postal", t("sd.postal-code", "Postal code"))}
              {field("phone", t("sd.phone", "Phone"))}
              {field("email", t("column.sd.email", "Email"))}
            </div>
          </>
        )}

        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <h3 className="text-[13px] font-semibold">{t("sd.new.parts", "Parts to send")}</h3>
          <ServicePartsPicker saleNumber={internal || nexera?.no == null ? null : Number(nexera.no)} picked={picked} onChange={setPicked} />
          {chosen.length > 0 && (
            <LineDetailsFields motives={(motives.data ?? []).filter((motive) => motive.is_active)} value={details} onChange={setDetails} />
          )}
        </div>
      </div>
    </Dialog>
  );
}
